import * as FileSaver from 'file-saver';
import * as Bixby from '$/core';

export abstract class CompanyService {
  private static portal = 'portal';
  private static baseUrl = Bixby.Constants.API_COMPANYCONTROLLER;
  private static adminBaseUrl = `${Bixby.Constants.API_ADMIN_PREFIX}/${Bixby.Constants.API_COMPANYCONTROLLER}`;

  // Client
  public static async createDealNote(
    clientCompanyId: string,
    model: Bixby.Models.CreateNote
  ): Promise<Bixby.Models.DealNote[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${clientCompanyId}/${Bixby.Constants.API_ACTION_COMPANY_CLIENT_DEAL_NOTES}`
    );

    return await Bixby.API.post(route, model);
  }

  public static async getDealNotes(clientCompanyId: string): Promise<Bixby.Models.DealNote[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${clientCompanyId}/${Bixby.Constants.API_ACTION_COMPANY_CLIENT_DEAL_NOTES}`
    );

    return await Bixby.API.get(route);
  }

  public static async getPortfolio(myPortfolioEnabled: boolean): Promise<Bixby.Models.CompanyPortfolio[]> {
    const route = myPortfolioEnabled
      ? Bixby.API.ServerRoute.forAction(`${CompanyService.baseUrl}/${Bixby.Constants.API_ACTION_MYPORTFOLIO}`)
      : Bixby.API.ServerRoute.forAction(`${CompanyService.baseUrl}/${Bixby.Constants.API_ACTION_PORTAL}/${Bixby.Constants.API_ACTION_PORTFOLIO}`);

    return await Bixby.API.get(route);
  }

  public static async exportPortfolio(myPortfolioEnabled: boolean): Promise<Blob> {
    const route = myPortfolioEnabled
      ? Bixby.API.ServerRoute.forAction(
        `${CompanyService.baseUrl}/${Bixby.Constants.API_ACTION_MYPORTFOLIO}/${Bixby.Constants.API_ACTION_DOWNLOAD}`
      )
      : Bixby.API.ServerRoute.forAction(
        `${CompanyService.baseUrl}/${Bixby.Constants.API_ACTION_PORTFOLIO}/${Bixby.Constants.API_ACTION_DOWNLOAD}`
      );

    return await Bixby.API.getFile(route);
  }

  public static async getTransactionComps(): Promise<Bixby.Models.CompanyTransactionComp[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${CompanyService.portal}/${Bixby.Constants.API_ACTION_TRANSACTION_COMPS}`
    );
    return await Bixby.API.get(route);
  }

  public static async getTransactionCompsByIndustryGroup(
    companyId: string
  ): Promise<Bixby.Models.CompanyTransactionComp[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${CompanyService.portal}/${Bixby.Constants.API_ACTION_TRANSACTION_COMPS}/${companyId}`
    );
    return await Bixby.API.get(route);
  }

  public static async getCompanyById(id: string, draft: boolean): Promise<Bixby.Models.Company> {
    if (draft) {
      try {
        const draftRoute = Bixby.API.ServerRoute.forAction(`${CompanyService.baseUrl}/${id}/draft`);
        return Object.assign({} as Bixby.Models.Company, await Bixby.API.get(draftRoute), { isDraft: draft });
      } catch {
        // suppress error
      }
    }

    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.baseUrl}/${CompanyService.portal}/${id}`);
    return Object.assign({} as Bixby.Models.Company, await Bixby.API.get(route), { isDraft: draft });
  }

  public static async getCompanyFinancialStatement(
    companyId: string,
    statementType: Bixby.Enums.FinancialStatementType
  ): Promise<string[]> {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.baseUrl}/${companyId}/${statementType}`);
    return await Bixby.API.get(route);
  }

  public static async exportPrimer(companyId: string): Promise<Blob> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${companyId}/${Bixby.Constants.API_ACTION_DOWNLOAD}/${Bixby.Constants.API_ACTION_PRIMER}`
    );
    return await Bixby.API.getFile(route);
  }

  public static async exportTearSheet(companyId: string): Promise<Blob> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${companyId}/${Bixby.Constants.API_ACTION_DOWNLOAD}/${Bixby.Constants.API_ACTION_TEARSHEET}`
    );
    return await Bixby.API.getFile(route);
  }

  public static async exportFinancialModel(companyId: string): Promise<Blob> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${companyId}/${Bixby.Constants.API_ACTION_DOWNLOAD}`
    );
    return await Bixby.API.getFile(route);
  }

  public static async exportHistoricalFinancialModel(companyId: string): Promise<Blob> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${companyId}/${Bixby.Constants.API_ACTION_DOWNLOAD}/${Bixby.Constants.API_ACTION_HISTORICAL_FINANCIAL_MODEL}`
    );
    return await Bixby.API.getFile(route);
  }

  public static async exportHistoricalPrimer(companyId: string): Promise<Blob> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${companyId}/${Bixby.Constants.API_ACTION_DOWNLOAD}/${Bixby.Constants.API_ACTION_HISTORICAL_PRIMER}`
    );
    return await Bixby.API.getFile(route);
  }

  public static async exportHistoricalTearSheet(companyId: string): Promise<Blob> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${companyId}/${Bixby.Constants.API_ACTION_DOWNLOAD}/${Bixby.Constants.API_ACTION_HISTORICAL_TEARSHEET}`
    );
    return await Bixby.API.getFile(route);
  }

  public static async saveClientEbitda(
    companyId: string,
    model: Bixby.Models.CompanyClientEbitda
  ): Promise<Bixby.Models.Company> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${companyId}/${Bixby.Constants.API_ACTION_COMPANY_CLIENT_EBITDA}`
    );

    return Object.assign({} as Bixby.Models.Company, await Bixby.API.post(route, model));
  }

  public static async setAlertsEnabled(companyId: string, alertsEnabled: boolean): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${companyId}/${Bixby.Constants.API_ACTION_COMPANY_ALERTS}/${alertsEnabled}`
    );
    await Bixby.API.put(route, null);
  }

  public static async getHistoricalCompany(companyId: string) {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${Bixby.Constants.API_ACTION_HISTORIES}/${companyId}`
    );
    return Object.assign({} as Bixby.Models.CompanyHistory, await Bixby.API.get(route));
  }

  public static async getHistoricalCompanies(): Promise<Bixby.Models.CompanyHistory[]> {
    const url = `${CompanyService.baseUrl}/${Bixby.Constants.API_ACTION_HISTORIES}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.get(route);
  }

  // Client/Admin
  public static async searchCompany(str: string): Promise<Bixby.Models.EntitySearchResult[]> {
    const sanitizedStr = encodeURIComponent(str);
    const url = `${CompanyService.baseUrl}/${Bixby.Constants.API_ACTION_SEARCH}`;
    const qs = `?${Bixby.Constants.API_PARAMS_PARTIALNAME}=${sanitizedStr}`;
    const route = Bixby.API.ServerRoute.forAction(`${url}${qs}`);
    return await Bixby.API.get(route);
  }

  // Admin
  public static async getForNews(id: string): Promise<Bixby.Models.AdminCompanyNews> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.adminBaseUrl}/${id}/${Bixby.Constants.API_ACTION_COMPANY_NEWS}`
    );
    return await Bixby.API.get(route);
  }

  public static async getAnalystNotes(companyId: string): Promise<Bixby.Models.AnalystNote[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.adminBaseUrl}/${companyId}/${Bixby.Constants.API_ACTION_COMPANY_ANALYST_NOTES}`
    );

    return await Bixby.API.get(route);
  }

  public static async createAnalystNote(
    companyId: string,
    model: Bixby.Models.CreateNote
  ): Promise<Bixby.Models.AnalystNote[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.adminBaseUrl}/${companyId}/${Bixby.Constants.API_ACTION_COMPANY_ANALYST_NOTES}`
    );

    return await Bixby.API.post(route, model);
  }

  public static async updateAnalystNote(companyId: string, analystNoteId: string, command: Bixby.Models.UpdateNote): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}/${companyId}/${Bixby.Constants.API_ACTION_COMPANY_ANALYST_NOTES}/${analystNoteId}`);
    await Bixby.API.put(route, command);
  }

  public static async deleteAnalystNote(companyId: string, analystNoteId: string): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}/${companyId}/${Bixby.Constants.API_ACTION_COMPANY_ANALYST_NOTES}/${analystNoteId}`);
    await Bixby.API.delete(route);
  }

  public static async getAll(): Promise<Bixby.Models.AdminCompanySlim[]> {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}`);
    return await Bixby.API.get(route);
  }

  public static async getAllForCompanyAdd(): Promise<Bixby.Models.AdminCompanyForClientAdd[]> {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}/${Bixby.Constants.API_ACTION_COMPANY_CLIENT_ADD}`);
    return await Bixby.API.get(route);
  }

  private static formatAdminCompany(adminCompany: Bixby.Models.AdminCompany): void {
    if (adminCompany && adminCompany.loanDetails && adminCompany.loanDetails.loans) {
      adminCompany.loanDetails.loans.forEach(l => (l.maturity = Bixby.Utils.parseUtcDateFromApi(l.maturity as any)));
    }
  }

  public static async createCompany(command: Bixby.Models.CreateCompanyRequest): Promise<Bixby.Models.AdminCompany> {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}`);
    const result: Bixby.Models.AdminCompany = await Bixby.API.post(route, command);
    CompanyService.formatAdminCompany(result);
    return result;
  }

  public static async getAdminCompany(id: string): Promise<Bixby.Models.AdminCompany> {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}/${id}`);
    const result: Bixby.Models.AdminCompany = await Bixby.API.get(route);
    CompanyService.formatAdminCompany(result);
    return result;
  }

  public static async removeCompany(id: string): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}/${id}`);
    return await Bixby.API.delete(route);
  }

  public static async importCompanies(companiesFile: File): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.adminBaseUrl}/${Bixby.Constants.API_ACTION_IMPORT}`
    );
    const formData = new FormData();
    formData.append('companiesFile', companiesFile);
    return await Bixby.API.post(route, formData);
  }

  public static async getImportTemplate(): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.adminBaseUrl}/${Bixby.Constants.API_ACTION_IMPORT_TEMPLATE}`
    );
    const blob = await Bixby.API.getFile(route);
    FileSaver.saveAs(blob, 'CompanyImportTemplate.xlsx');
  }

  public static async importLFIIds(lfiIdsFile: File): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.adminBaseUrl}/${Bixby.Constants.API_ACTION_IMPORT_LFI_IDS}`
    );
    const formData = new FormData();
    formData.append('lfiIdsFile', lfiIdsFile);
    return await Bixby.API.post(route, formData);
  }

  public static async getLFIImportTemplate(): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.adminBaseUrl}/${Bixby.Constants.API_ACTION_IMPORT_LFI_IDS_TEMPLATE}`
    );
    const blob = await Bixby.API.getFile(route);
    FileSaver.saveAs(blob, 'LFIIdsImportTemplate.xlsx');
  }

  public static async addClientCompany(clientId: string, companyId: string) {
    const client = `${Bixby.Constants.API_CLIENTCONTROLLER}/${clientId}`;
    const company = `${Bixby.Constants.API_ACTION_COMPANIES}/${companyId}`;
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${client}/${company}`);
    return await Bixby.API.post(route, null);
  }

  public static async deleteClientCompany(clientId: string, companyId: string) {
    const client = `${Bixby.Constants.API_CLIENTCONTROLLER}/${clientId}`;
    const company = `${Bixby.Constants.API_ACTION_COMPANIES}/${companyId}`;
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${client}/${company}`);
    return await Bixby.API.delete(route);
  }

  public static async generatePrimer(companyId: string, clientId: string | null): Promise<void> {
    let route: any = null;

    if (clientId === null) {
      route = Bixby.API.ServerRoute.forAction(
        `${CompanyService.adminBaseUrl}/${companyId}/${Bixby.Constants.API_ACTION_GENERATE}/${Bixby.Constants.API_ACTION_BIXBY}/${Bixby.Constants.API_ACTION_PRIMER}`
      );
    } else {
      route = Bixby.API.ServerRoute.forAction(
        `${CompanyService.adminBaseUrl}/${companyId}/${Bixby.Constants.API_ACTION_GENERATE}/${clientId}/${Bixby.Constants.API_ACTION_PRIMER}`
      );
    }

    return await Bixby.API.post(route, null);
  }

  public static async generateTearSheet(companyId: string, clientId: string | null): Promise<void> {
    let route: any = null;

    if (clientId === null) {
      route = Bixby.API.ServerRoute.forAction(
        `${CompanyService.adminBaseUrl}/${companyId}/${Bixby.Constants.API_ACTION_GENERATE}/${Bixby.Constants.API_ACTION_BIXBY}/${Bixby.Constants.API_ACTION_TEARSHEET}`
      );
    } else {
      route = Bixby.API.ServerRoute.forAction(
        `${CompanyService.adminBaseUrl}/${companyId}/${Bixby.Constants.API_ACTION_GENERATE}/${clientId}/${Bixby.Constants.API_ACTION_TEARSHEET}`
      );
    }

    return await Bixby.API.post(route, null);
  }

  public static async updateCompanyWatermarkStatus(companyId: string, isWatermarkLocked: boolean) {
    const action = `${companyId}/${Bixby.Constants.API_ACTION_WATERMARK_STATUS}`;
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}`, action);
    return await Bixby.API.post(route, { isWatermarkLocked });
  }

  public static async updateDocumentAnnouncement(companyId: string, announcement: string) {
    const action = `${companyId}/${Bixby.Constants.API_ACTION_DOCUMENT_ANNOUNCEMENT}`;
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}`, action);
    return await Bixby.API.post(route, { announcement });
  }

  public static async getComps(industryGroupId: string): Promise<Bixby.Models.CompanyPortfolio[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.baseUrl}/${Bixby.Constants.API_ACTION_COMPANY_COMPS}/${industryGroupId}`
    );
    return await Bixby.API.get(route);
  }

  // Compliance
  public static async getUsersByCompany(companyId): Promise<Bixby.Models.ComplianceUserCompany[]> {
    const action = `${companyId}/${Bixby.Constants.API_ACTION_USERS}`;
    const route = Bixby.API.ServerRoute.forAction(CompanyService.baseUrl, action);
    return await Bixby.API.get(route);
  }

  // Compliance
  public static async updateUsersByCompany(companyId: string, users: { user_Id: string, active: boolean }[]) {
    const action = `${companyId}/${Bixby.Constants.API_ACTION_USERS}`;
    const route = Bixby.API.ServerRoute.forAction(CompanyService.baseUrl, action);
    return await Bixby.API.post(route, users);
  }

  // Compliance
  public static async getDeclarationsForClientApproval(): Promise<Bixby.Models.ClientCompanyDeclarationViewModel[]> {
    const action = Bixby.Constants.API_ACTION_DECLARATIONS;
    const route = Bixby.API.ServerRoute.forAction(CompanyService.baseUrl, action);
    return await Bixby.API.get(route);
  }

  // Compliance
  public static async getDeclarationsForBixbyApproval(): Promise<Bixby.Models.AdminCompanyDeclarationViewModel[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.adminBaseUrl}/${Bixby.Constants.API_ACTION_DECLARATIONS}`
    );
    return await Bixby.API.get(route);
  }

  // Compliance
  public static async getAllWithDeclarationsForClient(): Promise<Bixby.Models.DeclarationPortfolio[]> {
    const action = Bixby.Constants.API_ACTION_PORTFOLIO_DECLARATIONS;
    const route = Bixby.API.ServerRoute.forAction(CompanyService.baseUrl, action);
    return await Bixby.API.get(route);
  }

  public static async getMostRecentCompanyTask(companyId: string): Promise<Bixby.Models.ClientCompanyTask | null> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.adminBaseUrl}/${companyId}/${Bixby.Constants.API_ACTION_TASK}`
    );
    try {
      const result = await Bixby.API.get<Bixby.Models.ClientCompanyTask>(route);
      return result || null;
    } catch {
      return null;
    }
  }

  public static async createCompanyTask(request: Bixby.Models.CompanyTaskCreationRequest) {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}/${Bixby.Constants.API_ACTION_TASK}`);
    const result: Bixby.Models.AdminCompany = await Bixby.API.post(route, request);
    return result;
  }

  public static async getCompanyPaiDataAsync(companyId: string): Promise<Bixby.Models.Pai> {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.baseUrl}/${companyId}/sfdr`);
    const result: Bixby.Models.Pai = await Bixby.API.get(route);
    return result;
  }

  public static async createDedicatedFinancialsAsync(companyId: string) {
    const route = Bixby.API.ServerRoute.forAction(
      `${CompanyService.adminBaseUrl}/${companyId}/${Bixby.Constants.API_ACTION_CREATE_DEDICATED_FINANCIALS}`
    );
    const result: Bixby.Models.AdminCompany = await Bixby.API.post(route, null);
    return result;
  }

  public static async getCompanyIssuerLevelMappingAsync(): Promise<Bixby.Models.CompanyIssuerLevelMapping[]> {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}/${Bixby.Constants.API_ACTION_GET_COMPANY_ISSUER_LEVEL_MAPPING}`);
    const result: Bixby.Models.CompanyIssuerLevelMapping[] = await Bixby.API.get(route);
    return result;
  }

  public static async updateCompanyIssuerLevelMappingAsync(command: Bixby.Models.CompanyIssuerLevelMappingUpdate[]) {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}/${Bixby.Constants.API_ACTION_UPDATE_COMPANY_ISSUER_LEVEL_MAPPING}`);
    await Bixby.API.put(route, command);
  }

  public static async getPackageIdLookupAsync(request: Bixby.Models.CreditSightsPackageIdLookupRequest) {
    const route = Bixby.API.ServerRoute.forAction(`${CompanyService.adminBaseUrl}/${Bixby.Constants.API_ACTION_PACKAGEID_LOOKUP}`);
    const result: Bixby.Models.CreditSightsPackageIdLookupResponse[] = await Bixby.API.post(route, request);
    return result;
  }
}
